import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import TutorialsService from "services/TutorialsService";

const initialState = {
  tutorials: [],
  tutorial: null,
  loading: false,
  error: null,
};

export const getTutorials = createAsyncThunk(
  "tutorials/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.getTutorials();
      return response.data.tutorials;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const getTutorialById = createAsyncThunk(
  "tutorials/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.getTutorialById(id);
      return response.data.tutorial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const createTutorial = createAsyncThunk(
  "tutorials/create",
  async (data, { rejectWithValue }) => {
    try {
      console.log("Starting API request with input data :", data);
      const response = await TutorialsService.createTutorial(data);
      console.log("API response:", response);
      return response.data.tutorial;
    } catch (error) {
      console.error("API error:", error);
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const updateTutorialById = createAsyncThunk(
  "tutorials/updateTutorialById",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.updateTutorial(id, data);
      return response.data.tutorial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const deleteTutorialById = createAsyncThunk(
  "tutorials/deleteTutorialById",
  async (id, { rejectWithValue }) => {
    try {
      await TutorialsService.deleteTutorialById(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const changeTutorialStatus = createAsyncThunk(
  "tutorials/changeStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.changeStatus(id, status);
      return response.data.tutorial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const changeTutorialVisibility = createAsyncThunk(
  "tutorials/changeVisibility",
  async ({ id, visibility }, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.changeVisibility(id, visibility);
      return response.data.tutorial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

// Ajout du thunk pour valider un tutoriel
export const validateTutorial = createAsyncThunk(
  "tutorials/validate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.validateTutorial(id);
      return response.data.tutorial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

// Ajout du thunk pour rejeter un tutoriel
export const rejectTutorial = createAsyncThunk(
  "tutorials/reject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.rejectTutorial(id, reason);
      return response.data.tutorial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

export const addStudentToTutorial = createAsyncThunk(
  "tutorials/addStudent",
  async ({ tutorialId, students }, { rejectWithValue }) => {
    try {
      const response = await TutorialsService.addStudentToTutorial(
        tutorialId,
        { students }
      );
      return response.data?.tutorial || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Error");
    }
  }
);

const tutorialsSlice = createSlice({
  name: "tutorials",
  initialState,
  reducers: {
    setTutorials: (state, { payload }) => {
      state.tutorials = payload;
    },
    setTutorial: (state, { payload }) => {
      state.tutorial = payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ALL
      .addCase(getTutorials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTutorials.fulfilled, (state, { payload }) => {
        state.tutorials = payload;
        state.loading = false;
      })
      .addCase(getTutorials.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // GET BY ID
      .addCase(getTutorialById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTutorialById.fulfilled, (state, { payload }) => {
        state.tutorial = payload;
        state.loading = false;
      })
      .addCase(getTutorialById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // CREATE
      .addCase(createTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTutorial.fulfilled, (state, { payload }) => {
        state.tutorials.push(payload);
        state.loading = false;
      })
      .addCase(createTutorial.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // UPDATE
      .addCase(updateTutorialById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTutorialById.fulfilled, (state, { payload }) => {
        state.tutorials = state.tutorials.map((tutorial) =>
          tutorial._id === payload._id ? payload : tutorial
        );
        state.loading = false;
      })
      .addCase(updateTutorialById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // DELETE
      .addCase(deleteTutorialById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTutorialById.fulfilled, (state, { payload }) => {
        state.tutorials = state.tutorials.filter(
          (tutorial) => tutorial._id !== payload
        );
        state.loading = false;
      })
      .addCase(deleteTutorialById.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // CHANGE STATUS
      .addCase(changeTutorialStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeTutorialStatus.fulfilled, (state, { payload }) => {
        state.tutorials = state.tutorials.map((tutorial) =>
          tutorial._id === payload._id ? payload : tutorial
        );
        state.loading = false;
      })
      .addCase(changeTutorialStatus.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // CHANGE VISIBILITY
      .addCase(changeTutorialVisibility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeTutorialVisibility.fulfilled, (state, { payload }) => {
        state.tutorials = state.tutorials.map((tutorial) =>
          tutorial._id === payload._id ? payload : tutorial
        );
        state.loading = false;
      })
      .addCase(changeTutorialVisibility.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })
      // Validation du tutoriel
      .addCase(validateTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateTutorial.fulfilled, (state, { payload }) => {
        state.tutorials = state.tutorials.map((tutorial) =>
          tutorial._id === payload._id ? payload : tutorial
        );
        state.loading = false;
      })
      .addCase(validateTutorial.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      })

      // Rejet du tutoriel
      .addCase(rejectTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectTutorial.fulfilled, (state, { payload }) => {
        state.tutorials = state.tutorials.map((tutorial) =>
          tutorial._id === payload._id ? payload : tutorial
        );
        state.loading = false;
      })
      .addCase(rejectTutorial.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });

    builder
      .addCase(addStudentToTutorial.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addStudentToTutorial.fulfilled, (state, { payload }) => {
        if (payload?._id) {
          state.tutorials = state.tutorials.map((tutorial) =>
            tutorial._id === payload._id ? payload : tutorial
          );
        }
        state.loading = false;
      })
      .addCase(addStudentToTutorial.rejected, (state, { payload }) => {
        state.error = payload;
        state.loading = false;
      });
  },
});

export const { setTutorials, setTutorial, setLoading, setError } =
  tutorialsSlice.actions;

export default tutorialsSlice.reducer;
